import { useQuery } from "@apollo/client";
import { themes } from "@aurem/tailwind-config/theme";
import { TypedKeys } from "@aurem/utils";
import type { FC, PropsWithChildren } from "react";
import { createContext, useContext } from "react";
import { EnvironmentQuery, EnvironmentTheme, gql } from "./types";

export type PlatformConfig = EnvironmentQuery["findEnvironmentConfig"];

const defaultPlatformConfig: PlatformConfig = {
  name: "Aurem",
  nameAr: "Aurem",
  firstLogin: true,
  blockedCitizenships: [],
};

const PlatformConfigContext = createContext<PlatformConfig>(
  defaultPlatformConfig,
);

export function usePlatformConfig(): PlatformConfig {
  return useContext(PlatformConfigContext);
}

const environmentQuery = gql(/* GraphQL */ `
  query Environment {
    findEnvironmentConfig {
      id
      name
      nameAr
      logoUrlRegular
      logoUrlInverse
      logoUrlSymbol
      themeName
      privacyNoticeEn
      privacyNoticeAr
      feelingLostUrlEn
      feelingLostUrlAr
      firstLogin
      blockedCitizenships
    }
  }
`);

function mergeFlatObjects<
  B extends Record<string, unknown>,
  O extends Record<string, unknown>,
  R extends {
    [K in keyof B | keyof O]: K extends keyof B
      ? B[K]
      : K extends keyof O
        ? O[K]
        : never;
  },
>(base: B, other?: O): R {
  const merged = { ...base } as unknown as R;
  if (other == null) return merged;
  for (const key of Object.keys(other)) {
    const val = other[key];
    if (val == null || val === "") continue;
    // @ts-expect-error Crazy object type magic
    merged[key] = val;
  }
  return merged;
}

type RGB = {
  r: number;
  g: number;
  b: number;
};

function hexToRGB(hex: string): RGB {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, "");
  // Double up shorthand hex notation
  if (hex.length === 3) {
    hex = Array.from({ length: 3 })
      .map((_, i) => hex[i] + hex[i])
      .join("");
  }
  hex = hex.padEnd(6, "0");
  // Parse the r, g, b values
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return { r, g, b };
}

/**
 * Tailwind requires css variables to be defined as colour channels in order to work with the opacity modifier syntax
 * @see https://tailwindcss.com/docs/customizing-colors#using-css-variables=
 */
function printVar(hex: string): string {
  const { r, g, b } = hexToRGB(hex);
  return `${r} ${g} ${b}`;
}

export function setColours(
  themeName: EnvironmentTheme,
  target?: HTMLElement,
): void {
  const root = target ?? (document.querySelector(":root") as HTMLElement);
  if (!root) return;

  const style = root.style;
  const theme = themes[themeName] ?? themes[EnvironmentTheme.Black];
  TypedKeys(theme).forEach((colour) => {
    TypedKeys(theme[colour]).forEach((shade) => {
      style.setProperty(
        `--color-${colour}-${shade}`,
        printVar(theme[colour][shade]),
      );
    });
  });
}

function loadEnvConfig(envConfig?: PlatformConfig): PlatformConfig {
  if (document == null) {
    return defaultPlatformConfig;
  }

  setColours(envConfig?.themeName ?? EnvironmentTheme.Black);
  // Overrides theme colours for backwards compatibility

  const platformConfig = mergeFlatObjects(defaultPlatformConfig, {
    name: envConfig?.name,
    id: envConfig?.id,
    nameAr: envConfig?.nameAr || envConfig?.name,
    logoUrlRegular: envConfig?.logoUrlRegular,
    logoUrlInverse: envConfig?.logoUrlInverse,
    logoUrlSymbol: envConfig?.logoUrlSymbol,
    privacyNoticeAr: envConfig?.privacyNoticeAr,
    privacyNoticeEn: envConfig?.privacyNoticeEn,
    feelingLostUrlEn: envConfig?.feelingLostUrlEn,
    feelingLostUrlAr: envConfig?.feelingLostUrlAr,
    firstLogin: envConfig?.firstLogin ?? true,
    blockedCitizenships: envConfig?.blockedCitizenships,
  });
  return platformConfig;
}

export const PlatformConfigProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data, loading } = useQuery(environmentQuery, {
    errorPolicy: "all",
  });
  const envConfig = data?.findEnvironmentConfig;

  const config = loadEnvConfig(envConfig);

  if (loading) {
    return null;
  }

  return (
    <PlatformConfigContext.Provider value={config}>
      {children}
    </PlatformConfigContext.Provider>
  );
};
